import type { Dict } from './en';

/**
 * French UI strings. French typography: a narrow no-break space (U+202F, invisible in most
 * editors) before `:` `?` `!` `%` and inside « guillemets ». tests/unit/i18n.test.ts checks it.
 */
export const fr: Dict = {
  // ---- common ----
  'common.back': '← Retour',
  'common.cancel': 'Annuler',
  'common.save': 'Enregistrer',
  'common.close': 'Fermer',
  'common.edit': 'Modifier',
  'common.delete': 'Supprimer',
  'common.remove': 'Retirer',
  'common.change': 'Changer',
  'common.keep': 'Garder',
  'common.paste': 'Coller',
  'common.add': '+ Ajouter',
  'common.other': 'Autre',
  'common.tryAgain': 'Réessayer',
  'common.on': 'Oui',
  'common.off': 'Non',
  'common.settings': 'Réglages',
  'common.loading': 'Chargement…',

  // ---- sign up / log in ----
  'auth.title': 'Bienvenue\nsur Tally.',
  'auth.lead': 'Tally transforme une phrase, un message vocal ou un ticket en dépenses. Votre clé Gemini reste sur votre appareil.',
  'auth.note': 'Connectez-vous avec votre compte Google. Tally ne reçoit que votre adresse e-mail.',
  'auth.google': 'Continuer avec Google',
  'auth.privacy': 'Politique de confidentialité',
  'auth.err.cancelled': 'Connexion annulée. Réessayez quand vous voulez.',
  'auth.err.failed': 'Google n’a pas pu vous connecter. Réessayez.',
  'auth.err.signups_disabled': 'Les inscriptions sont fermées sur ce Tally.',
  'auth.err.offline': 'Vous êtes hors ligne.',
  // ---- privacy ----
  'privacy.title': 'Confidentialité',
  'privacy.lead': 'Ce que Tally conserve, et où.',
  'privacy.account': 'Votre compte',
  'privacy.accountText': 'Vous vous connectez avec Google. Tally conserve l’identifiant stable et l’adresse e-mail de votre compte Google, rien d’autre. Votre mot de passe Google ne passe jamais par Tally.',
  'privacy.entries': 'Vos dépenses',
  'privacy.entriesText': 'Dépenses, catégories, réglages et appareils avec notifications sont stockés dans la base de Tally, pour votre compte seulement. Ils servent à afficher votre journal et à envoyer les notifications que vous avez activées.',
  'privacy.gemini': 'Gemini',
  'privacy.geminiText': 'Texte, messages vocaux et photos de tickets partent de votre navigateur directement vers l’API Gemini de Google, avec votre propre clé. La clé reste sur votre appareil, et le serveur de Tally ne voit ni la clé, ni l’audio, ni les photos. Seules les dépenses que vous confirmez sont enregistrées.',
  'privacy.cookies': 'Cookies',
  'privacy.cookiesText': 'Un cookie vous garde connecté pendant 30 jours, un autre vit dix minutes le temps de la connexion. Pas de statistiques, pas de scripts tiers, pas de publicité.',
  'privacy.push': 'Notifications push',
  'privacy.pushText': 'Si vous les activez, l’abonnement créé par votre navigateur est conservé et chaque message est chiffré de bout en bout. Retirez un appareil ou coupez les notifications à tout moment dans les réglages.',
  'privacy.yours': 'Vos données sont à vous',
  'privacy.yoursText': 'Exportez tout en CSV depuis l’aperçu. Supprimer votre compte dans les réglages efface tout d’un coup. Le code source est public.',

  // ---- setup ----
  'setup.step': 'Étape {n} / {total}',
  'setup.keyTitle': 'Apportez\nvotre clé.',
  'setup.keyLead': 'Tally utilise Gemini pour lire ce que vous tapez, dites ou photographiez. Collez une clé de Google AI Studio. La version gratuite suffit.',
  'setup.keyLabel': 'Clé API Google AI Studio',
  'setup.keyPlaceholder': 'Collez votre clé',
  'setup.getKey': 'Obtenir une clé sur aistudio.google.com →',
  'setup.keyNote': 'Votre clé reste sur cet appareil et n’est envoyée qu’à Google.',
  'setup.continue': 'Continuer',
  'setup.defaultsTitle': 'Quelques\nréglages.',
  'setup.currency': 'Devise',
  'setup.language': 'Langue',
  'setup.budget': 'Budget',
  'setup.perMonth': '/ mois',
  'setup.noBudget': 'Aucun',
  'setup.categoriesHint': 'Catégories — touchez pour retirer',
  'setup.newCategory': 'Nouvelle catégorie',
  'setup.start': 'Commencer',
  'setup.saveFailed': 'Impossible d’enregistrer. Réessayez.',
  'lang.auto': 'Auto (EN / FR)',
  'lang.en': 'English',
  'lang.fr': 'Français',

  // ---- Gemini key check ----
  'key.checking': 'Vérification de la clé…',
  'key.ok': 'La clé fonctionne · {model}',
  'key.invalid_key': 'Clé refusée par Google',
  'key.model_not_found': 'Modèle introuvable : {model}',
  'key.network': 'Pas de réseau',
  'key.quota': 'Trop de requêtes. Réessayez dans une minute.',
  'key.other': 'Impossible de vérifier la clé',

  // ---- home ----
  'home.spent': 'Dépensé ce mois-ci',
  'home.ofBudget': '{pct} % de {budget}',
  'home.daysLeft_one': '{count} jour restant',
  'home.daysLeft_other': '{count} jours restants',
  'home.lastDay': 'Dernier jour',
  'home.today': 'Aujourd’hui',
  'home.yesterday': 'Hier',
  'home.empty': 'Rien de noté pour l’instant. Tapez une ligne ou touchez le micro.',
  'home.showMonth': 'Voir {month} →',
  'home.monthEmpty': 'Rien de noté.',
  'home.loadFailed': 'Impossible de charger vos dépenses.',

  // ---- composer ----
  'composer.label': 'Décrivez une dépense',
  'composer.placeholder': 'Café 4.50 — ou dites-le',
  'composer.camera': 'Photographier ou choisir un ticket',
  'composer.mic': 'Enregistrer un message vocal',
  'composer.send': 'Envoyer',

  // ---- capture sheet ----
  'capture.sheet': 'Nouvelle dépense',
  'capture.listening': 'Écoute · {time}',
  'capture.releaseToSend': 'Relâchez pour envoyer',
  'capture.tapToStop': 'Touchez pour arrêter',
  'capture.stopSend': 'Arrêter et envoyer',
  'capture.thinkingListen': 'Gemini écoute…',
  'capture.thinkingRead': 'Gemini lit…',
  'capture.voiceNote': 'Message vocal · {time}',
  'capture.photo': 'Photo du ticket',
  'capture.youWrote': 'Vous avez écrit',
  'capture.youSaid': 'Vous avez dit',
  'capture.quote': '« {text} »',
  'capture.parsed': 'Lu par Gemini',
  'capture.ai': 'IA',
  'capture.gemini': 'Gemini',
  'capture.amount': 'Montant',
  'capture.what': 'Quoi',
  'capture.category': 'Catégorie',
  'capture.when': 'Quand',
  'capture.note': 'Note',
  'capture.optional': 'Facultatif',
  'capture.found_one': '{count} dépense trouvée',
  'capture.found_other': '{count} dépenses trouvées',
  'capture.total': 'Total',
  'capture.log_one': 'Noter {count} dépense',
  'capture.log_other': 'Noter {count} dépenses',
  'capture.include': 'Inclure {name}',
  'capture.editRow': 'Modifier {name}',
  'capture.typeIt': 'Le taper',
  'capture.noReply': 'Gemini n’y a trouvé aucune dépense.',
  'capture.micDenied': 'Tally a besoin du micro. Autorisez-le dans les réglages du navigateur.',
  'capture.micError': 'Le micro n’a pas pu démarrer.',
  'capture.tooShort': 'Trop court pour entendre quoi que ce soit.',
  'capture.saveFailed': 'Impossible d’enregistrer. Réessayez.',
  'capture.invalidAmount': 'Vérifiez le montant.',
  'capture.invalidWhat': 'Indiquez ce que c’était.',
  'capture.invalidWhen': 'Choisissez une date et une heure.',
  'when.today': 'Aujourd’hui, {time}',
  'when.yesterday': 'Hier, {time}',
  'when.day': '{day}, {time}',

  // ---- Gemini errors ----
  'gemini.invalid_key': 'Gemini a refusé votre clé. Vérifiez-la dans les réglages.',
  'gemini.quota': 'La version gratuite de Gemini n’a plus de requêtes pour l’instant. Réessayez dans une minute.',
  'gemini.network': 'Pas de connexion à Gemini.',
  'gemini.bad_response': 'Gemini a répondu quelque chose que je ne sais pas lire.',
  'gemini.model_not_found': 'Gemini ne connaît pas le modèle « {model} ». Vérifiez-le dans les réglages.',
  'gemini.unknown': 'Gemini n’a pas pu répondre. Réessayez.',
  'gemini.openSettings': 'Ouvrir les réglages →',

  // ---- entry sheet ----
  'entry.title': 'Modifier la dépense',
  'entry.delete': 'Supprimer la dépense',
  'entry.confirmDelete': 'Supprimer cette dépense ?',
  'entry.deleted': 'Dépense supprimée',
  'entry.undo': 'Annuler',
  'entry.saveFailed': 'Impossible d’enregistrer. Réessayez.',
  'entry.deleteFailed': 'Impossible de supprimer. Réessayez.',

  // ---- views (ledger | analytics) ----
  'nav.views': 'Vues',
  'nav.ledger': 'Journal',
  'nav.analytics': 'Analyse',

  // ---- overview ----
  'overview.week': 'Semaine',
  'overview.month': 'Mois',
  'overview.year': 'Année',
  'overview.periods': 'Période',
  'overview.prev': 'Période précédente',
  'overview.next': 'Période suivante',
  'overview.weekLabel': 'Semaine {week} · {range}',
  'overview.stats_one': '{count} dépense · moy. {avg} / jour',
  'overview.stats_other': '{count} dépenses · moy. {avg} / jour',
  'overview.statsYear': 'Moy. {avg} / mois',
  'overview.statsAhead_one': '{count} dépense à venir',
  'overview.statsAhead_other': '{count} dépenses à venir',
  'overview.delta': '{delta} %',
  'overview.deltaLabel': '{delta} % par rapport à {period}',
  'overview.compared': 'Évolution par rapport à {period}',
  'overview.export': 'Exporter en CSV →',
  'overview.empty': 'Rien de noté sur cette période.',
  'overview.loadFailed': 'Impossible de charger cette période.',
  'overview.askLabel': 'Interroger vos dépenses',
  'overview.askPlaceholder': 'Demandez : « combien en café ce mois-ci ? »',
  'overview.ask': 'Demander',
  'overview.askNoKey': 'Ajoutez votre clé Gemini dans les réglages pour poser des questions.',
  'overview.askThinking': 'Gemini réfléchit',
  'overview.share': '{share} %',
  'overview.shareLabel': '{share} % du total',
  'overview.openCategory': '{name} : dépenses et tendance',
  'overview.report': 'Rapport · {month} →',
  'overview.split': 'Fixe {fixed} · Courant {daily}',
  'overview.uncategorised_one': '{count} dépense sans catégorie · La classer →',
  'overview.uncategorised_other': '{count} dépenses sans catégorie · Les classer →',
  'overview.budgetMark': 'Budget {budget}',

  // ---- category (analytics drill-down) ----
  'category.ofPeriod': '{share} % de {period}',
  'category.entries_one': '{count} dépense',
  'category.entries_other': '{count} dépenses',
  'category.trend': '6 derniers mois',
  'category.loadFailed': 'Impossible de charger ces dépenses.',
  'category.avg': 'moy. {amount}',
  'category.budget': 'Budget mensuel',
  'category.noBudget': 'Aucun',
  'category.setBudget': 'Définir',
  'category.budgetPlaceholder': 'p. ex. 250',
  'category.left': '{amount} restants',
  'category.over': '{amount} de dépassement',
  'category.fixed': 'Charge fixe',
  'category.fixedHint': 'Loyer, factures, abonnements : comptés à part des dépenses courantes.',
  'category.otherHint': 'Touchez une dépense pour lui donner une catégorie.',

  // ---- monthly report ----
  'report.title': 'Rapport',
  'report.label': 'Rapport mensuel',
  'report.inProgress': 'Mois en cours',
  'report.prev': 'Mois précédent',
  'report.next': 'Mois suivant',
  'report.ofBudget': '{pct} % du budget de {budget}',
  'report.vsPrev': '{delta} % par rapport à {month} ({diff})',
  'report.noPrev': 'Rien de noté en {month} pour comparer.',
  'report.entries': 'Dépenses',
  'report.avgDay': 'Moy. / jour',
  'report.busiest': 'Jour le plus cher',
  'report.byCategory': 'Par catégorie',
  'report.biggest': 'Plus grosses dépenses',
  'report.history': '6 derniers mois',
  'report.pdf': 'Enregistrer en PDF',
  'report.empty': 'Rien de noté en {month}.',
  'report.loadFailed': 'Impossible de charger ce rapport.',
  'report.summarize': 'Résumer avec Gemini',
  'report.summaryThinking': 'Gemini écrit',
  'report.summaryLabel': 'Résumé',

  // ---- settings ----
  'settings.title': 'Réglages',
  'settings.gemini': 'Gemini',
  'settings.apiKey': 'Clé API',
  'settings.noKey': 'Aucune',
  'settings.model': 'Modèle',
  'settings.keyStored': 'Votre clé est stockée uniquement dans ce navigateur.',
  'settings.saveKey': 'Enregistrer la clé',
  'settings.defaults': 'Préférences',
  'settings.categories': 'Catégories',
  'settings.removeCategory': 'Retirer {name} ? Les dépenses passées seront classées dans Autre.',
  'settings.notifications': 'Notifications',
  'settings.notifDevice': 'Notifications sur cet appareil',
  'settings.notifUnsupported': 'Ce navigateur ne peut pas recevoir de notifications.',
  'settings.notifIos': 'Sur iPhone, ajoutez d’abord Tally à l’écran d’accueil, puis activez les notifications.',
  'settings.notifIosHow': 'Dans Safari, touchez Partager, puis « Sur l’écran d’accueil », et ouvrez Tally depuis l’écran d’accueil.',
  'settings.notifDenied': 'Les notifications sont bloquées pour Tally. Autorisez-les dans les réglages du navigateur.',
  'settings.notifFailed': 'Impossible d’activer les notifications.',
  'settings.reminder': 'Rappel quotidien',
  'settings.reminderTime': 'Heure du rappel',
  'settings.onlyIfEmpty': 'Seulement si rien n’a été noté',
  'settings.budgetAlerts': 'Alertes de budget',
  'settings.budgetAlertsHint': 'À 50 %, 80 % et 100 % du budget',
  'settings.weekly': 'Résumé de la semaine',
  'settings.weeklyHint': 'Le lundi matin',
  'settings.monthly': 'Bilan du mois',
  'settings.monthlyHint': 'Le premier jour du mois',
  'settings.test': 'Envoyer une notification de test',
  'settings.testSent_one': 'Envoyée à {count} appareil.',
  'settings.testSent_other': 'Envoyée à {count} appareils.',
  'settings.testNone': 'Aucun appareil ne l’a reçue.',
  'settings.devices': 'Appareils',
  'settings.thisDevice': 'Cet appareil',
  'settings.removeDevice': 'Retirer {name}',
  'settings.deviceSeen': 'Vu le {date}',
  'settings.unknownDevice': 'Appareil inconnu',
  'settings.deviceOn': '{browser} sur {os}',
  'settings.account': 'Compte',
  'settings.email': 'E-mail',
  'settings.signIn': 'Connexion',
  'settings.signInGoogle': 'Avec Google',
  'settings.logout': 'Se déconnecter',
  'settings.deleteAccount': 'Supprimer le compte',
  'settings.deleteWarning': 'Cela supprime votre compte et toutes vos dépenses. Tapez votre e-mail pour confirmer.',
  'settings.deleteForever': 'Supprimer définitivement',
  'settings.install': 'Installation',
  'settings.installButton': 'Installer Tally',
  'settings.installIos': 'Dans Safari, touchez Partager, puis « Sur l’écran d’accueil ».',
  'settings.installOther': 'Ouvrez le menu du navigateur et choisissez Installer, ou Ajouter à l’écran d’accueil.',
  'settings.about': 'À propos',
  'settings.version': 'Tally v{version}',
  'settings.source': 'Code source sur GitHub →',
  'settings.privacy': 'Politique de confidentialité →',
  'settings.saved': 'Enregistré.',
  'settings.saveFailed': 'Impossible d’enregistrer. Réessayez.',

  // ---- factures récurrentes ----
  'bills.title': 'Factures',
  'bills.billTitle': 'Facture',
  'bills.repeats': 'Revient',
  'bills.firstDue': 'Première échéance',
  'bills.paid': 'Payée',
  'bills.whenDue': 'À l’échéance',
  'bills.mode.auto': 'L’enregistrer pour moi',
  'bills.mode.ask': 'Me demander d’abord',
  'bills.mode.autoHint': 'Tally l’enregistre le jour même. Idéal pour un paiement par carte qui ne change pas.',
  'bills.mode.askHint': 'Tally vous le demande le jour même, et vous touchez Payée.',
  'bills.cadence.weekly': 'Chaque semaine',
  'bills.cadence.monthly': 'Chaque mois',
  'bills.cadence.quarterly': 'Tous les 3 mois',
  'bills.cadence.yearly': 'Chaque année',
  'bills.once': 'Une seule fois',
  'bills.on.weekly': 'Chaque semaine, le {weekday}',
  'bills.on.monthly': 'Chaque mois, le {nth}',
  'bills.on.quarterly': 'Tous les 3 mois, le {nth}',
  'bills.on.yearly': 'Chaque année, le {date}',
  'bills.saveBill': 'Enregistrer la facture',
  'bills.saved': '{name} enregistrée · première le {date}',
  'bills.savedPaid': '{name} payée · prochaine le {date}',
  'bills.savedMany_one': '{count} facture enregistrée',
  'bills.savedMany_other': '{count} factures enregistrées',
  'bills.logged': 'Enregistrée · prochaine le {date}',
  'bills.dueToday': 'À payer aujourd’hui',
  'bills.overdue': 'À payer depuis le {date}',
  'bills.notYet': 'Pas encore',
  'bills.paidIt': 'Payée',
  'bills.nextToday': 'aujourd’hui',
  'bills.nextTomorrow': 'demain',
  'bills.next': 'Prochaine facture',
  'bills.nextMore_one': 'Prochaine facture · encore {count} ce mois-ci',
  'bills.nextMore_other': 'Prochaine facture · encore {count} ce mois-ci',
  'bills.logsItself': 'S’enregistre seule',
  'bills.looksLike': 'On dirait une facture',
  'bills.seenSame': '{days}, même montant',
  'bills.notABill': 'Pas une facture',
  'bills.keep': 'En faire une facture',
  'bills.prevMonth': 'Mois précédent',
  'bills.nextMonth': 'Mois suivant',
  'bills.paidSum': 'Payé',
  'bills.toPay': 'Reste à payer',
  'bills.found_one': '{count} trouvée',
  'bills.found_other': '{count} trouvées',
  'bills.nothingDue': 'Rien à payer',
  'bills.addHint': 'Dites une facture pour la mettre à ce jour.',
  'bills.empty': 'Pas encore de facture. Dites-en une, par exemple « Loyer 1250 chaque mois le 1er ».',
  'bills.loadFailed': 'Impossible de charger vos factures.',
  'bills.open': 'Ouvrir {name}',
  'bills.day': '{date} : {amount}',
  'bills.dayEmpty': '{date} : rien à payer',
  'bills.next3': 'Prochaines',
  'bills.inDays_one': 'dans {count} jour',
  'bills.inDays_other': 'dans {count} jours',
  'bills.history': 'Payée via Tally',
  'bills.noHistory': 'Encore rien payé via Tally.',
  'bills.skip': 'Sauter celle-ci',
  'bills.payNow': 'Marquer payée',
  'bills.stop': 'Arrêter cette facture',
  'bills.confirmStop': 'Arrêter {name} ? Ce qu’elle a déjà payé reste dans votre registre.',
  'bills.stopped': 'Facture arrêtée',
  'bills.failed': 'Impossible pour le moment. Réessayez.',
  'bills.since': 'Depuis le {date}',
  'bills.perMonth': 'Environ {amount} par mois',
  'bills.notFound': 'Cette facture n’existe plus.',
  'bills.editTitle': 'Modifier la facture',
  'bills.spotted': 'Repérée dans vos dépenses',
  'bills.nextDue': 'Prochaine échéance',
  'bills.foundTitle': 'On dirait des factures',
  'bills.foundLead': 'Même chose, même prix, à intervalle régulier. Rien ne devient une facture sans votre accord.',
  'bills.noneFound': 'Rien à passer en revue.',
  'bills.fromBill': 'D’une facture',
  'composer.billPlaceholder': 'Une facture le {date} — ou parlez',
  'nav.bills': 'Factures',

  // ---- toasts ----
  'toast.offline': 'Vous êtes hors ligne.',
  'toast.updateReady': 'Mise à jour prête',
  'toast.reload': 'Recharger',
  'toast.install': 'Ajoutez Tally à l’écran d’accueil',
  'toast.installAction': 'Installer',
  'toast.error': 'Quelque chose n’a pas marché.',
};
